import { useEffect, useRef } from 'react';
import { useCompletedProjectQuery } from '@base/store/api';
import { getEnvironmentSettings } from '@utils/environmentConfig';

const config = getEnvironmentSettings();

// Create a singleton video element that persists across renders
let videoElement: HTMLVideoElement | null = null;
let currentProjectId: string | null = null;

export const useVideoPlayer = (containerId: string) => {
    const { data: projects, isFetching } = useCompletedProjectQuery();
    const containerRef = useRef<HTMLDivElement>(null);

    // Get the latest project name
    const latestProject = projects
        ? [...projects].sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime())[0]
        : null;

    useEffect(() => {
        if (isFetching || !projects || projects.length === 0) return;

        // Sort projects by date and get the latest one
        const sortedProjects = [...projects].sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime());
        const [latestProject] = sortedProjects;

        if (!latestProject?.video?.url) return;

        // Create video element if it doesn't exist
        if (!videoElement) {
            videoElement = document.createElement('video');
            videoElement.controls = true;
            videoElement.className = 'w-100';
            videoElement.style.maxHeight = '400px';
            videoElement.style.objectFit = 'contain';
            videoElement.style.height = 'auto';
            videoElement.style.maxWidth = '100%';

            const source = document.createElement('source');
            source.type = 'video/mp4';
            videoElement.appendChild(source);
        }

        // Only update source if we have a new project
        if (currentProjectId !== latestProject.id) {
            const videoUrl = `${config.blob.baseUrl}file?${latestProject.video.url}`;
            const source = videoElement.querySelector('source');
            if (source) {
                source.src = videoUrl;
                videoElement.load(); // Force load only when source changes
            }
            currentProjectId = latestProject.id;
        }

        // Move video element to current container
        if (containerRef.current) {
            // Only move if it's not already in this container
            if (videoElement.parentElement !== containerRef.current) {
                containerRef.current.innerHTML = '';
                containerRef.current.appendChild(videoElement);
            }
        }

        return () => {
            // On unmount, move the video element to a hidden container instead of removing it
            const hiddenContainer = document.getElementById('hidden-video-container');
            if (hiddenContainer && videoElement && videoElement.parentElement !== hiddenContainer) {
                hiddenContainer.innerHTML = '';
                hiddenContainer.appendChild(videoElement);
            }
        };
    }, [projects, isFetching, containerId]);

    return {
        containerRef,
        projectName: latestProject?.name,
        projectId: latestProject?.id,
    };
};

// This component just maintains a hidden container for the video when not in view
export const PersistentVideoPlayer = () => {
    return <div id="hidden-video-container" style={{ display: 'none' }} />;
};

export default PersistentVideoPlayer;
